import React from 'react';

import { FormattedMessage } from 'react-intl';
import { ofType } from 'redux-observable';
import { EMPTY, of, merge, timer, concat } from 'rxjs';
import { mergeMap, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { ModalService } from 'ui-components/services/modal-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import { getBulkDutiesCount$ } from '../../bulk/update/bulk.update.job.observables';
import { emptyArray } from 'ui-components/default-props/empty';
import { LabelBatchStatusStep } from '../../../enums/label-batch-status-step';
import { shouldShowPrepayDutyAlert } from '../../../fulfillment-planner/duties-and-taxes/helpers';
import { DHLTermsAndConditionsService } from '../../../services/dhl-terms-and-conditions-service';
import { REMOVE_FULFILLMENT_PLANS_FROM_BATCH_SUCCESS } from '../../../store/batches/details/batch.details.actions';
import * as EntitiesActions from '../../../store/entities/entities.actions';
import Global from '../../../store/global-selectors';
import { ORDER_DETAILS_MODAL_CLOSED } from '../../../store/orders/order-details/orders.details.actions';
import { promptShipstationDHLTermsAndConditions } from '../../../store/seller/seller.actions';
import { getQuickshipPropsFromState, determineShouldShowLabelBatchStatus, shouldLaunchLbsModal } from '../../quickship/quickship.helpers';
import { makeBatchLabelPurchaseObservable } from '../batches.label.observables';
import * as Actions from './batches.purchase.actions';
import {
  batchCostReviewApplyRecommendations,
  startBatchPurchaseCostReview,
  waitForEndOfCostReview,
} from './batches.purchase.cost-review.observables';
import { handleApiSuccess, handleSuccessMessage, handleFailureMessage, redirectToOrdersGrid } from './batches.purchase.processing.helpers';
import {
  startBatchValidation,
  handleValidationApiResponse,
  waitForEndOfValidation,
  showWeAreProcessingNotification,
} from './batches.purchase.validation.observables';
import { BATCH_ENTITY_GROUP_NAME, BATCH_ENTITY_ID_ATTRIBUTE } from './constants';
import { normalizeDataArray, buildEntityPayload } from './data-helpers';
import { messages } from './messages';

const TRIGGER_COST_REVIEW = 'TRIGGER_COST_REVIEW';
const TRIGGER_LABEL_PURCHASE = 'TRIGGER_LABEL_PURCHASE';
const triggerCostReview = () => ({ type: TRIGGER_COST_REVIEW });
const triggerLabelPurchase = () => ({ type: TRIGGER_LABEL_PURCHASE });

const showPurchasingLabels = (dontHide = true) => {
  const props = {
    title: <FormattedMessage {...messages.purchasingBatchTitle} />,
    content: <FormattedMessage {...messages.purchasingBatchContent} />,
    notificationType: 'info',
    dontHide,
  };
  return NotificationService.showNotification({ ...props });
};

const showPurchasingSuccess = (warningsCount = 0, dontHide = true) => {
  const props = {
    title: <FormattedMessage {...messages.purchasingBatchesSuccessTitle} />,
    content: warningsCount ? (
      <FormattedMessage {...messages.purchasingBatchesSuccessWithWarningsContent} values={{ warningsCount }} />
    ) : (
      <FormattedMessage {...messages.purchasingBatchesSuccessContent} />
    ),
    notificationType: NOTIFICATION_TYPE_SUCCESS, // TODO: when warning color implemented, implement explicit warning notification
    dontHide,
    duration: 3000,
  };
  return NotificationService.showNotification({ ...props });
};

const showPurchasingFailed = (dontHide = true) => {
  const props = {
    title: <FormattedMessage {...messages.purchasingHadOnlyErrorsTitle} />,
    content: <FormattedMessage {...messages.purchasingHadOnlyErrorsContent} />,
    notificationType: NOTIFICATION_TYPE_ERROR,
    dontHide,
    duration: 3000,
  };
  return NotificationService.showNotification({ ...props });
};

const showPurchasingMixed = (successCount, errorCount, dontHide = true) => {
  const props = {
    title: <FormattedMessage {...messages.purchasingHadSomeErrorsTitle} />,
    content: <FormattedMessage {...messages.purchasingHadSomeErrorsContent} values={{ successCount, errorCount }} />,
    notificationType: NOTIFICATION_TYPE_ERROR, // TODO: convert to warn when it exists
    dontHide,
    duration: 3000,
  };
  return NotificationService.showNotification({ ...props });
};

const showLowShipsuranceBalanceError = (dontHide = true) => {
  const props = {
    title: <FormattedMessage {...messages.purchasingHadOnlyErrorsTitle} />,
    content: <FormattedMessage {...messages.purchasingBatchLowShipsuranceBalance} />,
    notificationType: NOTIFICATION_TYPE_ERROR,
    dontHide,
    duration: 3000,
  };
  return NotificationService.showNotification({ ...props });
};

const showApiError = (dontHide = true) => {
  const props = {
    title: <FormattedMessage {...messages.purchasingHadOnlyErrorsTitle} />,
    content: <FormattedMessage {...messages.apiError} />,
    notificationType: NOTIFICATION_TYPE_ERROR,
    dontHide,
    duration: 3000,
  };
  return NotificationService.showNotification({ ...props });
};

const validation = ({ action$, getState, fulfillmentPlanSetId, quickshipProps }) => {
  return merge(
    merge(
      timer(0),
      action$.pipe(
        ofType(REMOVE_FULFILLMENT_PLANS_FROM_BATCH_SUCCESS, ORDER_DETAILS_MODAL_CLOSED, Actions.VALIDATE_BATCHES_RETRY),
        takeUntil(
          action$.pipe(
            ofType(
              // may need to change this as refactor continues
              Actions.BATCH_STATUS_MODAL_CLOSED, // this is for if the user cancels, we don't want to continue this path
            ),
            take(1),
          ),
        ),
      ),
    ).pipe(
      switchMap(() => {
        return startBatchValidation({
          action$,
          getState,
          fulfillmentPlanSetId,
        });
      }),
    ),
    handleValidationApiResponse({
      action$,
      getState,
      fulfillmentPlanSetId,
      quickshipProps,
    }),
    waitForEndOfValidation(action$).pipe(
      mergeMap(() => {
        return concat(of(triggerCostReview()), redirectToOrdersGrid({ fulfillmentPlanSetId, quickshipProps }));
      }),
    ),
  );
};

const costReview = ({ action$, getState, fulfillmentPlanSetId, quickshipProps }) => {
  let appliedSavings = {};

  const onApplyRecommendationsSuccess = (amountSaved) => {
    appliedSavings = amountSaved;
    return of(Actions.validateBatchesRetry(fulfillmentPlanSetId));
  };

  return action$.pipe(
    ofType(TRIGGER_COST_REVIEW, Actions.COST_REVIEW_BATCHES_RETRY),
    mergeMap(() => {
      return merge(
        startBatchPurchaseCostReview({
          action$,
          getState,
          fulfillmentPlanSetId,
          quickshipProps,
          appliedSavings,
        }),
        batchCostReviewApplyRecommendations(action$, getState, onApplyRecommendationsSuccess),
        waitForEndOfCostReview(action$).pipe(
          takeUntil(action$.pipe(ofType(Actions.BATCH_STATUS_MODAL_CLOSED), take(1))),
          mergeMap(() => {
            return of(triggerLabelPurchase());
          }),
        ),
      );
    }),
  );
};

const labelPurchase = ({ action$, getState, fulfillmentPlanSetId, shouldPrint }) => {
  const quickshipProps = getQuickshipPropsFromState(getState());
  const isPersonalShipsuranceAccount = Global.selectSellerPersonalShipsuranceAccount(getState());
  let purchasingNotificationId = '';
  return action$.pipe(
    ofType(TRIGGER_LABEL_PURCHASE),
    mergeMap(() => {
      if (!shouldLaunchLbsModal(quickshipProps)) {
        purchasingNotificationId = showPurchasingLabels();
      }
      return makeBatchLabelPurchaseObservable({
        fulfillmentPlanSetId,
        action$,
        isPersonalShipsuranceAccount,
        getState,
      }).pipe(
        mergeMap((messageArray) => {
          const { apiSuccess, apiErrorContext, labelBatchIds } = messageArray[0];
          const lbsModalShown = determineShouldShowLabelBatchStatus(quickshipProps);
          NotificationService.removeNotification(purchasingNotificationId);
          purchasingNotificationId = '';

          if (apiSuccess) {
            return handleApiSuccess({
              getState,
              fulfillmentPlanSetId,
              labelBatchIds,
              quickshipProps,
            });
          }

          if (apiErrorContext) {
            if (lbsModalShown) {
              ModalService.closeModalById(Global.selectBatchesPurchaseLabelBatchStatusModalId(getState()));
            }

            const isShipsuranceBalanceLow = apiErrorContext.isShipsuranceBalanceLow;
            if (isShipsuranceBalanceLow) {
              showLowShipsuranceBalanceError();
              return EMPTY;
            }

            showApiError();
            return EMPTY;
          }

          const showLabelBatchStatusNoto = !lbsModalShown;
          const allElementsSucceeded = messageArray.every((el) => !!el.finalMessage);
          const totalCompletedInFinalMessageCount = messageArray.reduce(
            (acc, current) => acc + (current.finalMessage?.status?.Completed ?? 0),
            0,
          );
          if (allElementsSucceeded) {
            if (showLabelBatchStatusNoto) {
              const totalWarningFinalMessageCount = messageArray.reduce((acc, current) => acc + (current.status?.Warnings ?? 0), 0);
              showPurchasingSuccess(totalWarningFinalMessageCount);
            }

            return handleSuccessMessage({
              getState,
              fulfillmentPlanSetId,
              completedCount: totalCompletedInFinalMessageCount,
              shouldPrint,
              quickshipProps,
            });
          }

          const totalErrorShipmentsInErrorMessageCount = messageArray.reduce(
            (acc, current) => acc + (current.errorMessage?.status?.Errors ?? 0),
            0,
          );
          if (showLabelBatchStatusNoto) {
            const totalCompletedShipmentsInErrorMessageCount = messageArray.reduce(
              (acc, current) => acc + (current.errorMessage?.status?.Completed ?? 0),
              0,
            );

            if (totalCompletedShipmentsInErrorMessageCount) {
              showPurchasingMixed(totalCompletedShipmentsInErrorMessageCount, totalErrorShipmentsInErrorMessageCount);
            } else {
              showPurchasingFailed();
            }
          }

          return handleFailureMessage({
            getState,
            completedCount: totalErrorShipmentsInErrorMessageCount,
            labelBatchIds,
            fulfillmentPlanSetId,
            shouldPrint,
            quickshipProps,
          });
        }),
      );
    }),
  );
};

const handleRetry = (action$, getState) => {
  return action$.pipe(
    ofType(Actions.VALIDATE_BATCHES_RETRY, Actions.COST_REVIEW_BATCHES_RETRY),
    switchMap(({ type, fulfillmentPlanSetId }) => {
      const existingEntity = Global.selectEntitiesBatchByFulfillmentPlanSetId(getState(), { fulfillmentPlanSetId });

      let nextStep;
      if (type === Actions.VALIDATE_BATCHES_RETRY) {
        nextStep = LabelBatchStatusStep.Validating.ordinal;
      } else if (type === Actions.COST_REVIEW_BATCHES_RETRY) {
        nextStep = LabelBatchStatusStep.CostReview.ordinal;
      } else {
        nextStep = existingEntity.step;
      }

      const batchEntityToUpdate = {
        ...existingEntity,
        step: nextStep,
        error: undefined,
      };
      const normalizedBatches = normalizeDataArray(BATCH_ENTITY_GROUP_NAME, BATCH_ENTITY_ID_ATTRIBUTE, [batchEntityToUpdate]);
      const entities = buildEntityPayload(normalizedBatches);
      return of(EntitiesActions.entitiesLoad(entities));
    }),
  );
};

export const startBatchesShipmentPurchase = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.BATCHES_START_SHIPMENT_PURCHASE),
    switchMap((action) => {
      const { fulfillmentPlanSetId, shouldPrint } = action;

      if (!fulfillmentPlanSetId) {
        return EMPTY;
      }

      const state = getState();
      const quickshipProps = getQuickshipPropsFromState(state);

      const fulfillmentPlanIds = Global.selectOrdersGridOrders(state) || emptyArray;
      const fulfillmentPlans =
        Global.selectEntitiesFulfillmentPlansByIds(state, {
          fulfillmentPlanIds,
        }) || emptyArray;
      const shouldShowShipstationTerms = DHLTermsAndConditionsService.sellerHasDhlWalletPurchaseAndNotAcceptedTerms(
        state,
        fulfillmentPlans,
      );
      if (shouldShowShipstationTerms) {
        const postAcceptanceAction = Actions.startBatchesShipmentPurchase(fulfillmentPlanSetId, shouldPrint);
        return of(promptShipstationDHLTermsAndConditions(postAcceptanceAction));
      }

      // TODO: need entities - different sets of batches in different parts of the UI
      const openBatches = Global.selectBatchesBatchRows(state);
      const matchingBatchFromGrid = openBatches.find((b) => b.fulfillmentPlanSetId === fulfillmentPlanSetId);
      const matchingBatchFromLeftNav = Global.selectBatchesNavNavBatchByFulfillmentPlanSetId(state, {
        fulfillmentPlanSetId,
      });
      const matchingBatch = matchingBatchFromGrid || matchingBatchFromLeftNav || {};

      const batchData = {
        fulfillmentPlanSetId,
        fulfillmentPlanIds: [],
        validateJobIds: [],
        costReviewJobIds: [],
        createLabelJobIds: [],
        shouldPrint,
        step: LabelBatchStatusStep.Validating.ordinal,
        batchName: matchingBatch.name,
        totalLabels: matchingBatch.shipmentCount,
      };

      let processingNotificationId = '';
      if (!shouldLaunchLbsModal(quickshipProps)) {
        processingNotificationId = showWeAreProcessingNotification();
      }

      const normalizedBatchData = normalizeDataArray(BATCH_ENTITY_GROUP_NAME, BATCH_ENTITY_ID_ATTRIBUTE, [batchData]);
      const entities = buildEntityPayload(normalizedBatchData);

      const startBatchPurchase$ = merge(
        of(EntitiesActions.entitiesLoad(entities)),
        validation({
          action$,
          getState,
          fulfillmentPlanSetId,
          quickshipProps,
        }),
        costReview({
          action$,
          getState,
          fulfillmentPlanSetId,
          quickshipProps,
        }).pipe(
          tap(() => {
            NotificationService.removeNotification(processingNotificationId);
            processingNotificationId = '';
          }),
        ),
        labelPurchase({
          action$,
          getState,
          fulfillmentPlanSetId,
          shouldPrint,
          quickshipProps,
        }),
        handleRetry(action$, getState),
      ).pipe(takeUntil(action$.pipe(ofType(Actions.BATCH_STATUS_MODAL_CLOSED), take(1))));

      if (shouldShowPrepayDutyAlert(state)) {
        const actionOf$ = startBatchPurchase$;
        const postAcceptanceAction = Actions.startBatchesShipmentPurchase(fulfillmentPlanSetId, shouldPrint);
        return getBulkDutiesCount$({
          actionOf$,
          fulfillmentPlanSetId,
          postAcceptanceAction,
          state,
        });
      }

      return startBatchPurchase$;
    }),
  );
};
